/* eslint-disable @typescript-eslint/no-unused-vars */
import { ChevronDownIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { actions } from '../../actions';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useToast } from '../../components/ui/use-toast';
import { services } from '../../services';
import { backendServices } from '../../services/backend.service';
import { createAdminNotification } from '../../services/notificationService';
import {
  Record,
  RentalPoolContract,
  ResellPoolContract,
  Web3Data,
} from '../../types/nft/approve.asset';

const ApproveButton: React.FC<{ record: Record }> = ({ record }) => {
  const { toast } = useToast();
  const [isPropertySet, setIsPropertySet] = useState<boolean>(false);
  const [isRentalSet, setIsRentalSet] = useState<boolean>(false);
  const [resellInstance, setResellInstance] = useState<ResellPoolContract | null>(null);
  const [rentalInstance, setRentalInstance] = useState<RentalPoolContract | null>(null);
  const [selectedAction, setSelectedAction] = useState<string | false>(false);
  const [approved, setApproved] = useState<boolean>(false);
  const [web3Data, setWeb3Data] = useState<Web3Data | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    handleApprove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [web3Data, record.assetAddress]);

  const handleApprove = async () => {
    try {
      setLoading(true);
      if (!web3Data) {
        const data = await services.getWeb3();
        if (data) {
          setWeb3Data(data);
        } else {
          throw new Error('Failed to get Web3 data');
        }
      } else {
        const resellPoolContractInstance = await actions.getResellPoolContract();
        const rentalPoolContractInstance = await actions.getRentalPoolContract();

        if (!resellPoolContractInstance || !rentalPoolContractInstance) {
          throw new Error('Failed to get contract instances');
        }

        setResellInstance(resellPoolContractInstance as ResellPoolContract);
        setRentalInstance(rentalPoolContractInstance as RentalPoolContract);

        const isPropertySet = await resellPoolContractInstance.methods
          .isPropertyAsset(record.assetAddress)
          .call();
        const isRentalSet = await rentalPoolContractInstance.methods
          .isPropertyListed(record.assetAddress)
          .call();

        setIsPropertySet(Boolean(isPropertySet));
        setIsRentalSet(Boolean(isRentalSet));

        if (isPropertySet) {
          setSelectedAction('Resell');
        } else if (isRentalSet) {
          setSelectedAction('Rental');
        }
      }
    } catch {
      // Error loading contract state
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySet = async () => {
    if (!resellInstance || !web3Data) return;
    try {
      setLoading(true);
      await resellInstance.methods.setPropertyAsset(record.assetAddress).send({
        from: web3Data.accounts[0],
      });
      // Call API to update asset resell status in database
      try {
        await backendServices.updateAssetResellStatus(record._id);
      } catch (apiError) {
        toast({
          description: 'Property set on resell, but failed to update database status',
          variant: 'destructive',
        });
      }
      setApproved(true);
      setIsPropertySet(true);
      setSelectedAction('Resell');
      toast({ description: 'Property set on resell', variant: 'default' });
      createAdminNotification({
        type: 'asset',
        title: 'Asset set on resell',
        message: `Asset ${record?.assetAddress ? `${record.assetAddress.slice(0, 10)}...` : 'Unknown'} was enabled for resell.`,
        priority: 'medium',
        assetId: record?._id,
      });
    } catch (error) {
      toast({ description: `${error}`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleRentalSet = async () => {
    if (!rentalInstance || !web3Data) return;
    try {
      setLoading(true);
      await rentalInstance.methods.setPropertyAddress(record.assetAddress).send({
        from: web3Data.accounts[0],
      });
      setApproved(true);
      setIsRentalSet(true);
      setSelectedAction('Rental');
      toast({ description: 'Property set on rental', variant: 'default' });
      createAdminNotification({
        type: 'asset',
        title: 'Asset set on rental',
        message: `Asset ${record?.assetAddress ? `${record.assetAddress.slice(0, 10)}...` : 'Unknown'} was enabled for rental.`,
        priority: 'medium',
        assetId: record?._id,
      });
    } catch (error) {
      toast({ description: `${error}`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (loading) return 'Approving...';
    if (isPropertySet) return 'On Resell';
    if (isRentalSet) return 'On Rental';
    return 'Set Now';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='default'
          className='w-24 flex items-center justify-between'
          disabled={loading}
          style={{ color: '#095D52' }}
        >
          {getButtonText()}
          <ChevronDownIcon className='h-4 w-4 ml-2' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-36'>
        <DropdownMenuItem
          onClick={handlePropertySet}
          disabled={isPropertySet}
          className={isPropertySet ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        >
          {isPropertySet ? 'On Resell' : 'Set as Resell'}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleRentalSet}
          disabled={isRentalSet}
          className={isRentalSet ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        >
          {isRentalSet ? 'On Rental' : 'Set as Rental'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ApproveButton;
